/**
 * Token Manager - Handles proactive token refresh and activity detection
 */

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.activityTimer = null;
    this.lastActivity = Date.now();
    this.isRefreshing = false;
    this.refreshThresholdMinutes = 5; // Refresh 5 minutes before expiration
    this.activityTimeoutMinutes = 30; // Consider user inactive after 30 minutes
    this.tokenExpirationHours = 8; // 8 hour tokens
    
    // Bind methods to preserve context
    this.handleActivity = this.handleActivity.bind(this);
    this.checkTokenExpiration = this.checkTokenExpiration.bind(this);
    this.refreshToken = this.refreshToken.bind(this);
    
    this.setupActivityListeners();
  }

  /**
   * Setup activity listeners to track user interaction
   */
  setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Handle user activity
   */
  handleActivity() {
    this.lastActivity = Date.now();
    
    // Reset activity timer
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    
    // Set new activity timer
    this.activityTimer = setTimeout(() => {
      console.log('🔄 User inactive for 30 minutes, stopping proactive refresh');
      this.stopProactiveRefresh();
    }, this.activityTimeoutMinutes * 60 * 1000);
  }

  /**
   * Start proactive token refresh
   */
  startProactiveRefresh() {
    console.log('🔄 Starting proactive token refresh system');
    
    // Clear any existing timer
    this.stopProactiveRefresh();
    
    // Calculate when to start refreshing (5 minutes before expiration)
    const refreshIntervalMs = (this.tokenExpirationHours * 60 - this.refreshThresholdMinutes) * 60 * 1000;
    
    console.log(`🔄 Will refresh token every ${refreshIntervalMs / 1000 / 60} minutes`);
    
    // Set up periodic refresh
    this.refreshTimer = setInterval(() => {
      this.checkTokenExpiration();
    }, refreshIntervalMs);
    
    // Also check immediately in case token is already close to expiration
    setTimeout(() => {
      this.checkTokenExpiration();
    }, 1000);
  }

  /**
   * Stop proactive token refresh
   */
  stopProactiveRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      console.log('🔄 Stopped proactive token refresh');
    }
    
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
  }

  /**
   * Check if token needs refreshing
   */
  async checkTokenExpiration() {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!token || !refreshToken) {
      console.log('🔄 No tokens found, stopping proactive refresh');
      this.stopProactiveRefresh();
      return;
    }

    // Check if user has been inactive
    const timeSinceActivity = Date.now() - this.lastActivity;
    const inactiveMinutes = timeSinceActivity / (1000 * 60);
    
    if (inactiveMinutes > this.activityTimeoutMinutes) {
      console.log(`🔄 User inactive for ${inactiveMinutes.toFixed(1)} minutes, skipping refresh`);
      return;
    }

    try {
      // Decode token to check expiration (basic check without verification)
      const tokenPayload = this.decodeTokenPayload(token);
      if (!tokenPayload) {
        console.log('🔄 Could not decode token payload');
        return;
      }

      const expirationTime = tokenPayload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;
      const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);

      console.log(`🔄 Token expires in ${minutesUntilExpiration.toFixed(1)} minutes`);

      // Refresh if token expires within threshold
      if (minutesUntilExpiration <= this.refreshThresholdMinutes) {
        console.log('🔄 Token approaching expiration, refreshing proactively...');
        await this.refreshToken();
      }
    } catch (error) {
      console.error('🔄 Error checking token expiration:', error);
    }
  }

  /**
   * Decode JWT token payload (client-side only, no verification)
   */
  decodeTokenPayload(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('🔄 Error decoding token:', error);
      return null;
    }
  }

  /**
   * Refresh the access token
   */
  async refreshToken() {
    if (this.isRefreshing) {
      console.log('🔄 Token refresh already in progress, skipping');
      return;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.log('🔄 No refresh token available');
      this.stopProactiveRefresh();
      return;
    }

    this.isRefreshing = true;

    try {
      console.log('🔄 Proactively refreshing access token...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const data = await response.json();
      const { access_token } = data;

      if (access_token) {
        localStorage.setItem('token', access_token);
        console.log('🔄 Access token refreshed successfully');
        
        // Dispatch custom event to notify other parts of the app
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
          detail: { access_token } 
        }));
      } else {
        throw new Error('No access token in refresh response');
      }
    } catch (error) {
      console.error('🔄 Proactive token refresh failed:', error);
      
      // If refresh fails, stop proactive refresh and let reactive system handle it
      this.stopProactiveRefresh();
      
      // Dispatch failure event
      window.dispatchEvent(new CustomEvent('tokenRefreshFailed', { 
        detail: { error: error.message } 
      }));
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Get time until token expiration in minutes
   */
  getTimeUntilExpiration() {
    const token = localStorage.getItem('token');
    if (!token) return 0;

    try {
      const payload = this.decodeTokenPayload(token);
      if (!payload) return 0;

      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;
      
      return Math.max(0, timeUntilExpiration / (1000 * 60));
    } catch (error) {
      console.error('Error calculating time until expiration:', error);
      return 0;
    }
  }

  /**
   * Check if user is currently active
   */
  isUserActive() {
    const timeSinceActivity = Date.now() - this.lastActivity;
    const inactiveMinutes = timeSinceActivity / (1000 * 60);
    return inactiveMinutes <= this.activityTimeoutMinutes;
  }

  /**
   * Cleanup - remove event listeners and timers
   */
  cleanup() {
    this.stopProactiveRefresh();
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

export default tokenManager;
